// Координаты под курсором — постоянная читалка в правом нижнем углу карты.
//
// Почему отдельной плашкой, а не в карточке: карточка отвечает на «что здесь»,
// а этот угол — на «где я». Второй вопрос задают непрерывно, ведя мышь вдоль
// кромки льда или вдоль изобаты, и открывать ради него карточку на каждый
// пиксель нельзя.
//
// НА КАРТУ ПОДПИСЫВАЕТСЯ САМА, а не принимает точку сверху. Курсор шлёт события
// на каждый пиксель, и подними мы их до страницы — «Модели» перерисовывались бы
// целиком по шестьдесят раз в секунду вместе со всеми слоями, легендами и
// карточками. Так перерисовывается один этот прямоугольник.
//
// ПРОЗРАЧНА ДЛЯ МЫШИ, кроме кнопки копирования: плашка стоит над картой, и
// перехваченный ею клик не доехал бы до карты — курсор, подведённый к самому
// углу, переставал бы ставить отметку.
import type maplibregl from 'maplibre-gl'
import { useEffect, useRef, useState } from 'react'
import { coordText, signedLon } from './pointCardView'
import { copyText } from './shareLink'
import { formatLatLon } from '../models/coords'
import { getLang, useT } from '../i18n'

interface Props {
  map: maplibregl.Map | null
}

/** Сколько держать итог копирования, прежде чем вернуть кнопку. */
const RESULT_MS = 1500

export function CoordReadout({ map }: Props) {
  const t = useT()
  const [point, setPoint] = useState<{ lng: number; lat: number } | null>(null)
  const [copied, setCopied] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current)
  }, [])

  useEffect(() => {
    if (!map) return
    const move = (e: maplibregl.MapMouseEvent) =>
      setPoint({ lng: e.lngLat.lng, lat: e.lngLat.lat })
    // `mouseout` полотна, а не `mouseleave` слоя: у MapLibre своего «курсор
    // покинул карту» нет, а плашка с координатами точки, которую уже не
    // показывает курсор, врёт молча.
    const out = () => setPoint(null)
    map.on('mousemove', move)
    map.on('mouseout', out)
    return () => {
      map.off('mousemove', move)
      map.off('mouseout', out)
    }
  }, [map])

  if (!point) return null

  const copy = async () => {
    // В буфер уходит НЕ то, что на плашке: там «57,6987° с.ш.» с локальной
    // запятой и словами, а в навигатор нужно «57.6987, 23.7696». Формат тот
    // же, что у переноса координат косяка (`formatLatLon`).
    //
    // «Скопировано» показываем только по факту. Планшет на мостике ходит по
    // http, Safari спрашивает разрешение — соврав, мы заставили бы вставить в
    // чат то, что лежало в буфере до этого (та же причина, что у «Поделиться»).
    if (!(await copyText(formatLatLon(point.lat, signedLon(point.lng))))) return
    setCopied(true)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setCopied(false), RESULT_MS)
  }

  return (
    <div
      aria-label={t('maps.point.readout')}
      className="pointer-events-none absolute bottom-6 right-4 z-10 flex items-center gap-2 rounded-lg border border-hull bg-abyss/85 px-2.5 py-1 shadow-console backdrop-blur"
    >
      <span className="tnum text-[11px] leading-none text-foam">
        {coordText(point.lat, point.lng, getLang())}
      </span>
      <button
        type="button"
        onClick={() => void copy()}
        title={t('maps.point.copy')}
        aria-label={t('maps.point.copy')}
        className="pointer-events-auto rounded px-1 text-[10px] leading-none text-mist transition-colors hover:text-foam"
      >
        {copied ? (
          t('maps.point.copied')
        ) : (
          <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.4">
            <rect x="5.5" y="5.5" width="7.5" height="7.5" rx="1.4" />
            <path d="M10.5 3.5H4a1.5 1.5 0 0 0-1.5 1.5v6.5" strokeLinecap="round" />
          </svg>
        )}
      </button>
    </div>
  )
}
